import { useState, useCallback } from 'react'
import type { BreadcrumbItem } from '@/lib/types'
import { buildCategoryHref, VIEW_PATHS } from '@/lib/navigation'
import { slugify } from '@/lib/slug'

interface CategoryNode {
  id: string
  name: string
  slug?: string
  parentId?: string
}

export function useBreadcrumb() {
  const [categoryPath, setCategoryPath] = useState<CategoryNode[]>([])

  const buildBreadcrumb = useCallback((
    type: 'home' | 'products' | 'product-detail' | 'cart' | 'quotes' | 'orders' | 'returns' | 'support' | 'about' | 'privacy' | 'terms' | 'careers' | 'compare',
    options?: {
      productName?: string
      onCategoryClick?: (categoryId: string) => void
      onProductsClick?: () => void
    }
  ): BreadcrumbItem[] => {
    const segments: BreadcrumbItem[] = []

    switch (type) {
      case 'products':
        if (categoryPath.length > 0) {
          const slugs: string[] = []
          categoryPath.forEach((cat) => {
            const slug = cat.slug || slugify(cat.name)
            slugs.push(slug)
            segments.push({
              label: cat.name,
              categoryId: cat.id,
              href: buildCategoryHref(slugs),
              onClick: options?.onCategoryClick ? () => options.onCategoryClick!(cat.id) : undefined,
            })
          })
        } else {
          segments.push({
            label: 'Produtos',
            href: VIEW_PATHS.products,
            onClick: options?.onProductsClick,
          })
        }
        break

      case 'product-detail':
        if (categoryPath.length > 0) {
          const slugs: string[] = []
          categoryPath.forEach((cat) => {
            const slug = cat.slug || slugify(cat.name)
            slugs.push(slug)
            segments.push({
              label: cat.name,
              categoryId: cat.id,
              href: buildCategoryHref(slugs),
              onClick: options?.onCategoryClick ? () => options.onCategoryClick!(cat.id) : undefined,
            })
          })
        } else {
          segments.push({
            label: 'Produtos',
            href: VIEW_PATHS.products,
            onClick: options?.onProductsClick,
          })
        }
        if (options?.productName) {
          segments.push({
            label: options.productName,
          })
        }
        break

      case 'cart':
        segments.push({
          label: 'Carrinho',
          href: VIEW_PATHS.checkout,
        })
        break

      case 'quotes':
        segments.push({
          label: 'Meus Orçamentos',
          href: VIEW_PATHS.quotes,
        })
        break

      case 'orders':
        segments.push({
          label: 'Meus Pedidos',
          href: VIEW_PATHS.orders,
        })
        break

      case 'returns':
        segments.push({
          label: 'Devoluções',
          href: VIEW_PATHS.returns,
        })
        break

      case 'support':
        segments.push({
          label: 'Atendimento',
          href: VIEW_PATHS.support,
        })
        break

      case 'about':
        segments.push({
          label: 'Quem somos',
          href: VIEW_PATHS.about,
        })
        break

      case 'privacy':
        segments.push({
          label: 'Política de privacidade',
          href: VIEW_PATHS.privacy,
        })
        break

      case 'terms':
        segments.push({
          label: 'Termos de uso',
          href: VIEW_PATHS.terms,
        })
        break

      case 'careers':
        segments.push({
          label: 'Trabalhe conosco',
          href: VIEW_PATHS.careers,
        })
        break

      case 'compare':
        segments.push({
          label: 'Comparar Produtos',
          href: VIEW_PATHS.compare,
        })
        break
    }

    return segments
  }, [categoryPath])

  const setCategoryBreadcrumb = useCallback((path: CategoryNode[]) => {
    setCategoryPath(path)
  }, [])

  const clearCategoryBreadcrumb = useCallback(() => {
    setCategoryPath([])
  }, [])

  return {
    buildBreadcrumb,
    categoryPath,
    setCategoryBreadcrumb,
    clearCategoryBreadcrumb,
  }
}
